import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  AssetMetaInterface,
  ComputedPageItem,
  ExperienceType,
  ObjectInterface,
  ProductLayout,
} from '@taopix/taopix-core/dist/types/types';
import {
  ArrowLeftTopIcon,
  BackgroundAllLeftIcon,
  BackgroundAllRightIcon,
  BackgroundAllSpreadsIcon,
  BackgroundIcon,
  BackgroundSpreadIcon,
  Button,
  ButtonGrid,
  ButtonWithPopout,
  CheckCircleIcon,
  CloseIcon,
  EditBoxIcon,
  Heading,
  HelpTip,
  Horizontal,
  MoreIcon,
  PopOut,
  ThemeName,
  Vertical,
} from '@taopix/taopix-design-system';
import {useSelector} from 'react-redux';
import {
  BoxLayer,
  COLOUR_PREFIX,
  CollectionType,
  Common,
  DEFAULT_BACKGROUND_COLOUR,
  NumBool,
  PageDetectionActionType,
  ViewMode,
} from '@taopix/taopix-core';
import {
  popDirectionTypes,
  PopOutProps,
} from '@taopix/taopix-design-system/dist/types/Components/PopOut/PopOut';
import BackgroundsList, {Tile} from '../BackgroundsList/BackgroundsList';
import {useTaopix} from '../Context/TaopixContext';
import {useTranslation} from 'react-i18next';
import {useExperience} from '../Experience/ExperienceProvider';
import useFilter from './useFilter';
import classNames from 'classnames';
import {KeyboardKeys} from '../../Enums';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import useSettings from '../../Hooks/useSettings';
import ToolPaletteSection from '../ToolPalette/ToolPaletteSection';
import {
  BackgroundCategoriesSelector,
  BackgroundsSelector,
  LayoutSelector,
  TotalPageCountSelector,
} from '../../Selectors';
import {onPageCountChanged} from '../../Utils/Page';
import {useUserFeedback} from '../Context/UserFeedback/UserFeedbackContext';
import {UserFeedbackActions} from '../Context/UserFeedback/UserFeedbackActionTypes';
import useKeyboardTipOnFocus from '../../Hooks/useKeyboardTipOnFocus';
import {ButtonProps} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';

export enum PictureSortType {
  DateTaken = 'datetimeoriginal',
  Filename = 'name',
  Size = 'filesizebytes',
  Orientation = 'orientation',
}

type helpTipStateType = {
  open: boolean;
  message: string;
  buttonLabel: string;
  buttonFunction: Function;
};

export type BackgroundPaletteProps = {
  sectionKey: string;
  item?: ComputedPageItem;
  viewMode: ViewMode;
};

const BackgroundPalette = React.forwardRef(
  ({sectionKey, item}: BackgroundPaletteProps, ref: React.ForwardedRef<HTMLButtonElement>) => {
    const app = useTaopix();
    const ui = useUiState();
    const settings = useSettings();
    const {experience, updateExperience} = useExperience();
    const userFeedback = useUserFeedback();
    const {t} = useTranslation(['toolpalette', 'helptips']);

    const [filter, setFilter] = useFilter<AssetMetaInterface & {isColour: boolean}>();
    const [filterPopOutOpen, setFilterPopOutOpen] = useState<boolean>(false);
    const [applyToAllPopoutOpen, setApplyToAllPopoutOpen] = useState(false);
    const [selectedButtonIndex, setSelectedButtonIndex] = useState(0);
    const [filterCount, setFilterCount] = useState<number>(0);
    const [introTipOpen, setIntroTipOpen] = useState<boolean>(false);
    const [loaded, setLoaded] = useState(false);
    const [isSinglePage, setIsSinglePage] = useState<boolean>(false);
    const [selectedBackground, setSelectedBackground] = useState<Tile | null>(null);
    const [showCantEditBackgroundPopout, setShowCantEditBackgroundPopout] =
      useState<boolean>(false);
    const [showCantEditColourPopout, setShowCantEditColourPopout] = useState<boolean>(false);
    const [gridState, setGridState] = useState<ObjectInterface>({
      orientation: 'vertical',
      columns: 2, // The column count is only used if orientation is vertical; in horizontal mode there are infinite columns
      tileSize: 60, // The tileSize is only used if orientation is horizontal; in vertical mode it is determined by the column count
      gap: 10,
    });
    const [helpTipState, setHelpTipState] = useState<helpTipStateType>({
      open: false,
      message: '',
      buttonLabel: '',
      buttonFunction: () => {},
    });

    const history = app.core.get('history');
    const pageCount = useSelector(TotalPageCountSelector);
    const backgrounds = useSelector(BackgroundsSelector);
    const productLayout = useSelector(LayoutSelector);
    const categories = useSelector(BackgroundCategoriesSelector);
    const filterButtonRef = useRef<HTMLButtonElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const isSmallScreen = ui.state.isSmallScreen;
    const backgroundColours = settings.state?.colours?.palette;

    const canDeviceDrag = matchMedia('(pointer: fine)').matches;

    useKeyboardTipOnFocus({elementId: 'backgroundPaletteContent', tipName: 'backgroundspalette'});

    // The popouts are anchored to their respective buttons on large screen, but anchored to the bottom of the backgrounds palette on small screen
    const popoutProps: Partial<PopOutProps> = {
      ...(isSmallScreen
        ? {
            className: 'w-bounds flex-col p-sm',
            offset: {x: 0, y: 0},
            anchorOrigin: {horizontal: Horizontal.Centre, vertical: Vertical.Bottom},
          }
        : {
            className: 'w-[var(--anchorElementWidth)] flex-col',
            offset: {x: 0, y: -5},
            anchorOrigin: {horizontal: Horizontal.Centre, vertical: Vertical.Top},
          }),
      displayMode: 'positioned',
      repositionOnResize: true,
      transformOrigin: {horizontal: Horizontal.Centre, vertical: Vertical.Bottom},
      popDirection: 'up' as popDirectionTypes,
      afterClose: () => {
        if (ui.state.busy) {
          ui.dispatch(UserInterfaceActions.setIsBusy(false));
        }
      },
    };

    // grab the palette exeprience config
    const perms = experience.EDITOR.backgrounds;

    // can we display the filters?
    const canFilter = perms.categoryFilter;

    const buttonCSS = 'mt-xs ml-xs';

    const hasCategories = Object.keys(categories).length > 1;
    const hasProjectBackgroundThemes = perms.designs && Object.keys(backgrounds).length > 0;
    const hasBackgroundColours =
      perms.colours && Object.values(settings.state?.colours?.palette || {}).length > 0;

    // if project has 1 page to determine wether to show apply to all button
    const hasOnePage = productLayout.pagecount === 1;

    // if the category experience option is on and the project has categories then show filter button otherwise dont
    const showFilterButton = canFilter && hasCategories;

    // If this is not a one page project and the apply to all button is allowed, then show it
    const showApplyToAllButton = !hasOnePage && perms.applyToAllPagesButton;

    const statePageNumber = ui.state.pageNumber;
    const paletteOpen = ui.state.palette.section?.includes(sectionKey) || false;

    const editingViewModes = [
      experience.EDITOR.workArea.editingViewModes.list,
      experience.EDITOR.workArea.editingViewModes.page,
    ];

    const showEditBackgroundButton =
      experience.EDITOR.backgrounds.editBackgroundButton &&
      editingViewModes.length > 0 &&
      hasProjectBackgroundThemes;

    const contentWrapperClasses = classNames(
      'flex',
      'lg:flex-1',
      'flex-col',
      'min-h-0',
      'sm:h-mobilePaletteContentHeight',
      'sm:mt-auto'
    );

    const thumbnailScrollContainerClasses = classNames(
      'flex-1',
      'flex',
      'flex-col',
      'lg:flex-1',
      'px-md',
      'pb-[40rem]',
      'overflow-y-auto'
    );

    const backgroundTabsContainerClasses = classNames(
      'flex',
      'px-md border-b border-b-themeTextColour20',
      'mb-md',
      'lg:space-x-xs',
      'sm:space-x-sm'
    );

    const applyButtonsContainerClasses = classNames(
      'flex',
      'm-md',
      'lg:flex-col',
      'lg:space-y-sm',
      'sm:space-x-sm',
      'sm:self-center',
      'sm:w-[min(400rem,theme(width.bounds))]'
    );

    const filtersPopoutClasses = classNames(
      isSmallScreen ? 'sm:w-[min(600rem,theme(width.bounds))]' : 'w-[300rem]'
    );

    const onSelectThemeTab = () => {
      setSelectedButtonIndex(0);
      setFilter([{isColour: false}]);
      setGridState({
        ...gridState,
        columns: 2,
        gap: isSmallScreen ? 5 : 10,
      });
      setFilterCount(0);
    };

    const onSelectColourTab = () => {
      setSelectedButtonIndex(1);
      setFilter([{isColour: true}, {categoryName: null}]);
      setGridState({
        ...gridState,
        columns: 4,
        gap: 5,
      });
      setFilterCount(0);
    };

    const onKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === KeyboardKeys.ESCAPE) {
        closeButtonRef.current?.focus();
      }
    };

    const onApplyToAllBackground = useCallback(
      (
        assetRef: string | number,
        detectionType: PageDetectionActionType,
        applyToAllString: string
      ) => {
        app.core
          .get('stateManager')
          .call('applyToAll', {
            type: 'backgrounds',
            ref: String(assetRef),
            pageDetection: detectionType,
          })
          .then(() => {
            // onSuccess show helpTip
            setApplyToAllPopoutOpen(false);

            showHelpTip(
              t(applyToAllString, {ns: 'helptips'}),
              CollectionType.Calendar !== productLayout.type
                ? t('*:str_ButtonUndo')
                : t('*:str_ButtonOK'),
              () => {
                if (history.canUndo() && CollectionType.Calendar !== productLayout.type) {
                  history.undo().then(response => {
                    ui.dispatch(
                      UserInterfaceActions.setHistoryState({
                        undo: history.canUndo(),
                        redo: history.canRedo(),
                      })
                    );
                    updateExperience(app.core.get('experience') as unknown as ExperienceType);

                    if (response.spineResized) {
                      onPageCountChanged(
                        {spineResized: response.spineResized},
                        ui,
                        response?.project.productLayout as ProductLayout,
                        pageCount
                      );
                    }
                  });
                }
                closeHelpTip();
              }
            );

            // if we are a single page we don't show the left/right/spread popout (which will modify the busy state on close)
            // if (isSinglePage) {
            ui.dispatch(UserInterfaceActions.setIsBusy(false));
            // }
          })
          .catch(error => {
            let message = app.core.get('server').getErrorFromResponse(error);
            if ('' === message || !message) {
              message = error;
            }
            ui.dispatch(UserInterfaceActions.setIsBusy(false));
            userFeedback.dispatch(
              UserFeedbackActions.setErrorUserFeedback({
                title: '',
                message,
                showHeader: true,
              })
            );
          });
      },
      [isSinglePage]
    );

    // if applying to single page we dont show popout and display message that the background has been applied
    const applyToAll = () => {
      if (!isSinglePage) {
        toggleApplyToAllPopout();
      } else if (selectedBackground) {
        onApplyToAllBackground(
          selectedBackground.assetRef,
          !isSinglePage ? PageDetectionActionType.AllBoth : PageDetectionActionType.AllLeft,
          !isSinglePage
            ? 'str_MessageBackgroundToAppliedAllSpreads'
            : 'str_MessageBackgroundAppliedToAllPages'
        );
      }
    };

    const toggleApplyToAllPopout = () => {
      setApplyToAllPopoutOpen(!applyToAllPopoutOpen);
    };

    const closeHelpTip = () => {
      setHelpTipState({
        ...helpTipState,
        open: false,
      });
    };

    const showHelpTip = (message: string, buttonLabel: string, fn?: () => void) => {
      setHelpTipState({
        open: true,
        message: message,
        buttonLabel: buttonLabel,
        buttonFunction: fn
          ? fn
          : () => {
              closeHelpTip();
            },
      });
    };

    const applyBackground = (newBackground: Tile) => {
      // Apply the clicked background to the page
      if ('' !== newBackground?.assetRef) {
        app.core
          .dispatch('PAGE.SET_BACKGROUND', {
            pageNumber: statePageNumber,
            assetId: String(newBackground?.assetRef),
            isRecordingHistory: true,
            skipRender: false,
          })
          .catch(error => {
            console.error(error);
          });
        // Update the slected background state so we can highlight it in the list
        setSelectedBackground(newBackground);
      }
    };

    const clearBackground = (newBackground: Tile) => {
      // Set the background to white
      app.core
        .dispatch('PAGE.SET_BACKGROUND', {
          pageNumber: statePageNumber,
          assetId: DEFAULT_BACKGROUND_COLOUR,
          isRecordingHistory: true,
          skipRender: false,
        })
        .catch(error => {
          console.error(error);
        });
      // Clear the selected background state to remove the selection highlight
      setSelectedBackground(null);
    };

    const filterButton = useMemo(() => {
      if (!canFilter) {
        return <></>;
      }
      return (
        <>
          {showFilterButton && (
            <Button
              label={t('*:str_LabelFilter')}
              hideLabel
              startIcon={<MoreIcon />}
              buttonStyle={'standard'}
              className={'mb-xs'}
              ref={filterButtonRef}
              id={'backgroundsFiltersButton'}
              onClick={() => {
                setFilterPopOutOpen(!filterPopOutOpen);
              }}
              aria-pressed={filterPopOutOpen || (filterCount as unknown as number) > 0}
              size={
                ui.state.isSmallScreen &&
                experience.EDITOR.toolPalette.mobileSlideUpPanels === 'msuo'
                  ? 'xsmall'
                  : 'small'
              }
              badgeContent={(filterCount as unknown as number) > 0 ? String(filterCount) : ''}
              badgeStyle={ThemeName.Primary}
              badgePosition="top-right"
              // disabled={selectedButtonIndex !== 0}
            />
          )}
          <PopOut
            open={paletteOpen && filterPopOutOpen}
            id={'backgroundsFiltersPopout'}
            anchorId={isSmallScreen ? 'backgroundsPalette' : 'backgroundsFiltersButton'}
            onClose={() => {
              setFilterPopOutOpen(false);
            }}
            excludeIdFromClickOutside={'backgroundsFiltersButton'}
            repositionOnResize={isSmallScreen}
            displayMode={'positioned'}
            offset={isSmallScreen ? {x: 0, y: 0} : {x: 5, y: 0}}
            anchorOrigin={
              isSmallScreen
                ? {vertical: Vertical.Bottom, horizontal: Horizontal.Centre}
                : {vertical: Vertical.Top, horizontal: Horizontal.Right}
            }
            transformOrigin={
              isSmallScreen
                ? {vertical: Vertical.Bottom, horizontal: Horizontal.Centre}
                : {vertical: Vertical.Top, horizontal: Horizontal.Left}
            }
            popDirection={isSmallScreen ? 'up' : 'right'}
            focusFirstElement={true}
            className={filtersPopoutClasses}
            theme={'tpx-theme-toolpaletteContent'}
            themeTip={'Sidebar (Content)'}
            contentTheme={ThemeName.Popouts}
          >
            <div
              className={'w-full p-sm'}
              onKeyDown={(event: React.KeyboardEvent) => {
                onKeyDown(event);
              }}
            >
              <>
                {/* close button for large screen filter popout */}
                {!isSmallScreen && (
                  <div className={'absolute top-xs right-xs'}>
                    <Button
                      label={t('toolpalette:str_LabelCloseFilterPopout')}
                      hideLabel
                      startIcon={<CloseIcon />}
                      onClick={() => {
                        setFilterPopOutOpen(false);
                        filterButtonRef.current?.focus();
                      }}
                      size={'xsmall'}
                      buttonStyle={'standard'}
                      ref={closeButtonRef}
                    />
                  </div>
                )}
                {selectedButtonIndex === 0 ? (
                  // Show the filters if we're on the theme tab
                  <>
                    <Heading level={1} className={'text-md mb-xs'} size={4}>
                      {t('toolpalette:str_LabelCategories')}
                    </Heading>
                    <ButtonGrid>
                      <>
                        <Button
                          label={t('*:str_LabelAll')}
                          buttonStyle={'tertiary'}
                          size={'small'}
                          className={buttonCSS}
                          aria-pressed={!filter?.categoryName || filter.categoryName === null}
                          onClick={() => {
                            setFilter([{categoryName: null}]);
                            setFilterCount(0);
                          }}
                        />
                        {Object.values(categories).map((item, index) => {
                          return (
                            <Button
                              key={index}
                              label={item.label}
                              buttonStyle={'tertiary'}
                              size={!isSmallScreen ? 'small' : 'medium'}
                              className={buttonCSS}
                              onClick={() => {
                                setFilter([{categoryName: item.value}]);
                                setFilterCount(1);
                              }}
                              aria-pressed={
                                filter?.categoryName && filter.categoryName === item.value
                              }
                            />
                          );
                        })}
                      </>
                    </ButtonGrid>
                    {isSmallScreen && (
                      <Button
                        buttonStyle={'secondary'}
                        label={t('*:str_ButtonDone')}
                        size="medium"
                        onClick={() => {
                          setFilterPopOutOpen(false);
                        }}
                        className={'w-full mt-md'}
                      />
                    )}
                  </>
                ) : (
                  // Don't show the filters if we're on the colours tab
                  <p>{t('*:str_MessageFilterNotAvailableForBackgroundColours')}</p>
                )}
              </>
            </div>
          </PopOut>
        </>
      );
    }, [canFilter, filterPopOutOpen, categories, filter]);

    // Switch the grid type when changing screen size
    useEffect(() => {
      setGridState({
        ...gridState,
        gap: isSmallScreen || selectedButtonIndex === 0 ? 5 : 10,
        orientation: isSmallScreen ? 'horizontal' : 'vertical',
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSmallScreen]);

    // Update the selected background tile when the selected side changes
    useEffect(() => {
      // Set the selected background
      setSelectedBackground({assetRef: ui.state.selectedSide.backgroundAssetId} as Tile);
      // Scroll to the selected tile
    }, [ui.state.selectedSide]);

    // Set the default category to designs. If there are no project backgrounds then set it to colours
    useEffect(() => {
      if (hasProjectBackgroundThemes && perms.designs) {
        onSelectThemeTab();
      } else if (hasBackgroundColours && perms.colours) {
        onSelectColourTab();
      }

      setIsSinglePage(productLayout.spreads === 1 ? false : true);
    }, []);

    useEffect(() => {
      const tokens: string[] = [];

      if (paletteOpen && loaded && canDeviceDrag) {
        setIntroTipOpen(true);
        // Close the openLayoutsPalette tip if it's open
        if (ui.state.tips.openLayoutsPalette) {
          ui.dispatch(UserInterfaceActions.setTipOpen({name: 'openLayoutsPalette', open: false}));
        }
      } else if (paletteOpen && !loaded) {
        setLoaded(true);
      } else if (!paletteOpen) {
        setIntroTipOpen(false);
      }

      if (!paletteOpen) {
        // Reset the selected tab when closing the palette
        if (hasProjectBackgroundThemes && selectedButtonIndex === 0) {
          onSelectThemeTab();
        } else if (hasBackgroundColours && selectedButtonIndex === 1) {
          onSelectColourTab();
        }
        setFilterCount(0);

        // Close the 'Can't edit background' help tip when closing the palette
        setShowCantEditBackgroundPopout(false);
      }

      return () => {
        tokens.forEach((token, index) => {
          app.core.unsubscribe(token);
          delete tokens[index];
        });
      };
    }, [paletteOpen, loaded]);

    useEffect(() => {
      // When opening the palette, if there is a non-background item selected, or
      // if, when the section is changed to something other than the backgrounds or a box-setting palette and a background IS selected
      // deselect the selected item
      if (
        (paletteOpen && item?.layer && item?.layer !== BoxLayer.Background) ||
        (item?.layer === BoxLayer.Background &&
          !['backgrounds', 'box-settings-palette'].includes(
            String(ui.state.palette.section?.split('/')[0])
          ))
      ) {
        app.core.dispatch('SHAPE.DESELECT', null).catch(error => {
          console.error(error);
        });
      }
    }, [ui.state.palette.section, paletteOpen]);

    const onSelectBackgroundKeyboard = useCallback(
      (event: React.KeyboardEvent, newSelectedBackground: Tile) => {
        /*
        Enter - Apply to current page
        1 -
          Spread pages - Apply to spread page
          Single pages  - Apply to all pages
        2 - ApplyToAll spread pages
        3 - ApplyToAll left pages
        4 - ApplyToAll right pages
        */
        const isSpreadProduct = NumBool.True === productLayout.spreads;

        // Invalid apply action
        if (!isSpreadProduct && ['2', '3', '4'].includes(event.key)) return;

        if (['Enter', '1', '2', '3', '4', '5'].includes(event.key)) {
          if (event.key === 'Enter') {
            applyBackground(newSelectedBackground);
            return;
          }

          if (event.key === '1' && isSpreadProduct && !pageHasNonAccessible()) {
            onApplyToSpread(newSelectedBackground);
            return;
          }

          const applyToAllConfig = {
            '1': {
              detectionType: PageDetectionActionType.AllLeft,
              confirmationString: 'str_MessageBackgroundAppliedToAllPages',
            },
            '2': {
              detectionType: PageDetectionActionType.AllBoth,
              confirmationString: 'str_MessageBackgroundAppliedToAllSpreads',
            },
            '3': {
              detectionType: PageDetectionActionType.AllLeft,
              confirmationString: 'str_MessageBackgroundAppliedToAllLeftPages',
            },
            '4': {
              detectionType: PageDetectionActionType.AllRight,
              confirmationString: 'str_MessageBackgroundAppliedToAllRightPages',
            },
          };

          const applyToAllAction = applyToAllConfig[event.key];
          if (applyToAllAction) {
            onApplyToAllBackground(
              newSelectedBackground.assetRef,
              applyToAllAction.detectionType,
              applyToAllAction.confirmationString
            );
          }
        }
      },
      [statePageNumber, productLayout.spreads, selectedBackground]
    );

    // does the experience allow us to show the palette?
    // completly hide if colours && designs off?

    if (
      !perms.backgroundsPalette ||
      (!perms?.colours && (!perms?.designs || !hasProjectBackgroundThemes))
    ) {
      return null;
    }

    const onEditBackground = () => {
      // If this is grid view, show a popout explaining that you need to switch to a different view to edit the background
      if (ui.state.viewMode === ViewMode.Grid && !productLayout.retroprints) {
        setShowCantEditBackgroundPopout(true);
      } else if (
        typeof selectedBackground?.assetRef === 'string' &&
        selectedBackground?.assetRef.startsWith(COLOUR_PREFIX)
      ) {
        setShowCantEditColourPopout(true);
      } else if (item?.layer !== BoxLayer.Background) {
        // If there isn't a background item selected, select the background item
        app.core
          .dispatch('PAGE.ACTIVATE_BACKGROUND', {
            pageNumber: statePageNumber,
          })
          .catch(error => console.error(error));
      } else {
        // Deselect the background item
        app.core.dispatch('SHAPE.DESELECT', null).catch(error => {
          console.error(error);
        });
      }
    };

    const onApplyToSpread = (newSelectedBackground?: Tile) => {
      // Apply the selected background image asset to the current spread
      const assetRef = newSelectedBackground
        ? newSelectedBackground?.assetRef
        : selectedBackground?.assetRef;

      app.core
        .dispatch('PAGE.SET_BACKGROUND', {
          assetId: String(assetRef),
          detectionArea: PageDetectionActionType.CurrentSpread,
          pageNumber: statePageNumber,
          isRecordingHistory: true,
          skipRender: false,
        })
        .catch(error => console.error(error));
    };

    const pageHasNonAccessible = (): boolean => {
      return (
        Common.pageHasNonAccessibleLeft(
          statePageNumber,
          productLayout.firstpagenumber,
          NumBool.True === productLayout.spreads
        ) ||
        Common.pageHasNonAccessibleRight(
          statePageNumber,
          productLayout.lastpage,
          NumBool.True === productLayout.spreads
        )
      );
    };

    const applyToAllButtonProps: Partial<ButtonProps> = {
      buttonStyle: 'primary',
      label:
        applyToAllPopoutOpen && !isSmallScreen
          ? t('*:str_ButtonClose')
          : isSinglePage
          ? t('*:str_ButtonApplytoAllPages')
          : t('*:str_ButtonApplyToEllipsis'),
      size:
        ui.state.isSmallScreen && experience.EDITOR.toolPalette.mobileSlideUpPanels === 'msuo'
          ? 'small'
          : 'medium',
      labelLines: 2,
      id: 'backgrounds-apply-to-all-button',
      disabled:
        selectedBackground === null ||
        ((selectedBackground?.assetRef as string).startsWith(COLOUR_PREFIX) &&
          selectedButtonIndex === 0) ||
        (!(selectedBackground?.assetRef as string).startsWith(COLOUR_PREFIX) &&
          selectedButtonIndex === 1) ||
        ui.state.selectedSide.imageMissing,
      className: 'flex-1',
    };

    const applyToAllButton = (
      <>
        {isSinglePage ? (
          <Button {...applyToAllButtonProps} onClick={() => applyToAll()} />
        ) : (
          <ButtonWithPopout
            open={applyToAllPopoutOpen}
            onOpen={() => {
              setApplyToAllPopoutOpen(true);
            }}
            onClose={() => {
              setApplyToAllPopoutOpen(false);
            }}
            returnFocusToTriggerOnItemClick
            buttonProps={applyToAllButtonProps}
            popoutProps={{
              id: 'backgrounds-apply-to-all-popout',
              anchorId: isSmallScreen ? 'backgroundsPalette' : 'backgrounds-apply-to-all-button',
              onClose: toggleApplyToAllPopout,
              excludeIdFromClickOutside: 'backgrounds-apply-to-all-button',
              theme: 'tpx-theme-toolpaletteContent',
              themeTip: 'Sidebar (Content)',
              contentTheme: ThemeName.Popouts,
              ...popoutProps,
            }}
          >
            {!selectedBackground?.isColour && (
              <>
                {!pageHasNonAccessible() && (
                  <Button
                    label={t('*:str_ButtonApplyToPageSpread')}
                    buttonStyle={'standard'}
                    corners={'square'}
                    labelAlignment={'left'}
                    startIcon={<BackgroundSpreadIcon />}
                    onClick={() => onApplyToSpread()}
                  />
                )}
                <Button
                  label={t('*:str_ButtonApplyToAllPageSpreads')}
                  buttonStyle={'standard'}
                  corners={'square'}
                  labelAlignment={'left'}
                  startIcon={<BackgroundAllSpreadsIcon />}
                  onClick={() => {
                    if (selectedBackground) {
                      onApplyToAllBackground(
                        selectedBackground.assetRef,
                        PageDetectionActionType.AllBoth,
                        'str_MessageBackgroundAppliedToAllSpreads'
                      );
                    }
                  }}
                />
              </>
            )}
            <Button
              label={t('*:str_ButtonApplyToAllLeftPages', {
                ns: 'toolpalette',
              })}
              buttonStyle={'standard'}
              corners={'square'}
              labelAlignment={'left'}
              startIcon={<BackgroundAllLeftIcon />}
              onClick={() => {
                if (selectedBackground) {
                  onApplyToAllBackground(
                    selectedBackground.assetRef,
                    PageDetectionActionType.AllLeft,
                    'str_MessageBackgroundAppliedToAllLeftPages'
                  );
                }
              }}
            />
            <Button
              label={t('*:str_ButtonApplyToAllRightPages', {
                ns: 'toolpalette',
              })}
              buttonStyle={'standard'}
              corners={'square'}
              labelAlignment={'left'}
              startIcon={<BackgroundAllRightIcon />}
              onClick={() => {
                if (selectedBackground) {
                  onApplyToAllBackground(
                    selectedBackground.assetRef,
                    PageDetectionActionType.AllRight,
                    'str_MessageBackgroundAppliedToAllRightPages'
                  );
                }
              }}
            />

            {/* Show a cancel button in the popout on small screen */}
            {isSmallScreen && (
              <Button
                buttonStyle={'secondary'}
                label={t('*:str_ButtonClose')}
                size="medium"
                onClick={toggleApplyToAllPopout}
                className={'m-sm'}
              />
            )}
          </ButtonWithPopout>
        )}

        <HelpTip
          open={helpTipState.open}
          message={helpTipState.message}
          buttonLabel={helpTipState.buttonLabel}
          buttonFunction={helpTipState.buttonFunction}
          anchorId={'viewerToolbar'}
          anchorOrigin={
            ui.state.isSmallScreen
              ? {vertical: Vertical.Bottom, horizontal: Horizontal.Centre}
              : {vertical: Vertical.Bottom, horizontal: Horizontal.Left}
          }
          transformOrigin={
            ui.state.isSmallScreen
              ? {vertical: Vertical.Top, horizontal: Horizontal.Centre}
              : {vertical: Vertical.Top, horizontal: Horizontal.Left}
          }
          offset={ui.state.isSmallScreen ? undefined : {x: 10, y: 0}}
          icon={<CheckCircleIcon />}
          showAfter={500}
          hideAfter={3000}
          onClose={() => {
            closeHelpTip();
          }}
        />
      </>
    );

    return (
      <ToolPaletteSection
        sectionKey={sectionKey}
        useFocusMode
        title={t('*:str_LabelBackgrounds')}
        buttonIcon={<BackgroundIcon />}
        id={'backgroundsPalette'}
        ref={ref}
        showSeparator={false}
        onToggle={() => {
          setApplyToAllPopoutOpen(false);
        }}
      >
        <div className={contentWrapperClasses} id={'backgroundPaletteContent'}>
          {hasBackgroundColours && hasProjectBackgroundThemes && (
            <div id="backgroundTabsContainer" className={backgroundTabsContainerClasses}>
              <Button
                id={'backgroundsThemeTab'}
                label={t('*:str_LabelTheme')}
                className={'flex-1' + (selectedButtonIndex === 0 ? ' -mb-[1rem]' : '')}
                buttonStyle={'tab'}
                tabPosition={'top'}
                size={
                  ui.state.isSmallScreen &&
                  experience.EDITOR.toolPalette.mobileSlideUpPanels === 'msuo'
                    ? 'xsmall'
                    : 'small'
                }
                onClick={onSelectThemeTab}
                aria-pressed={selectedButtonIndex === 0}
                labelAlignmentWhenWrapped="left"
              />
              <Button
                label={t('*:str_LabelColours')}
                className={'flex-1' + (selectedButtonIndex === 1 ? ' -mb-[1rem]' : '')}
                buttonStyle={'tab'}
                size={
                  ui.state.isSmallScreen &&
                  experience.EDITOR.toolPalette.mobileSlideUpPanels === 'msuo'
                    ? 'xsmall'
                    : 'small'
                }
                tabPosition={'top'}
                onClick={onSelectColourTab}
                aria-pressed={selectedButtonIndex === 1}
                labelAlignmentWhenWrapped="left"
              />
              {hasCategories && canFilter && <>{filterButton}</>}
            </div>
          )}
          <div id="backgroundsListContainer" className={thumbnailScrollContainerClasses}>
            <BackgroundsList
              visible={paletteOpen}
              parentSection={'backgrounds'}
              backgrounds={backgrounds}
              backgroundColours={backgroundColours}
              columns={selectedButtonIndex === 1 ? (isSmallScreen ? 6 : 5) : isSmallScreen ? 4 : 2}
              columnGap={selectedButtonIndex === 1 ? 3 : 5}
              filterValues={filter}
              orientation={gridState.orientation}
              selectedBackgroundRef={String(selectedBackground?.assetRef)}
              useHexCode={false}
              onClick={(
                event: React.MouseEvent | React.ChangeEvent,
                newSelectedBackground: Tile
              ) => {
                if (newSelectedBackground.assetRef !== selectedBackground?.assetRef) {
                  applyBackground(newSelectedBackground);
                } else {
                  clearBackground(newSelectedBackground);
                }
              }}
              onKeyDown={(event: React.KeyboardEvent, newSelectedBackground: Tile) => {
                onSelectBackgroundKeyboard(event, newSelectedBackground);
              }}
            />
          </div>

          {(showApplyToAllButton || showEditBackgroundButton) && (
            <div id="applyButtonsContainer" className={applyButtonsContainerClasses}>
              {showApplyToAllButton && applyToAllButton}
              {showEditBackgroundButton && (
                <>
                  <Button
                    label={t('*:str_ButtonEditBackground')}
                    id={'editBackgroundButton'}
                    startIcon={<EditBoxIcon />}
                    buttonStyle={'distinct'}
                    onClick={onEditBackground}
                    className={classNames(
                      'flex-1',
                      // Apply a disabled style if grid view or current background is a colour
                      ((ui.state.viewMode === ViewMode.Grid && !productLayout.retroprints) ||
                        (typeof selectedBackground?.assetRef === 'string' &&
                          selectedBackground?.assetRef.startsWith(COLOUR_PREFIX))) &&
                        '!cursor-not-allowed tds-disabled'
                    )}
                    aria-pressed={item?.layer === BoxLayer.Background}
                    size={
                      ui.state.isSmallScreen &&
                      experience.EDITOR.toolPalette.mobileSlideUpPanels === 'msuo'
                        ? 'small'
                        : 'medium'
                    }
                  />
                  <HelpTip
                    open={showCantEditBackgroundPopout}
                    id={'cantEditBackgroundMessage'}
                    message={t('*:str_MessageSwitchViewToEditBackground')}
                    anchorId={'viewerToolbar'}
                    anchorOrigin={{vertical: Vertical.Bottom, horizontal: Horizontal.Centre}}
                    transformOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Centre}}
                    buttonLabel={t('*:str_ButtonOK')}
                    buttonFunction={() => setShowCantEditBackgroundPopout(false)}
                  />
                  <HelpTip
                    open={showCantEditColourPopout}
                    id={'cantEditColourMessage'}
                    message={t('*:str_MessageCantEditColourBackground')}
                    anchorId={'editBackgroundButton'}
                    anchorOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Centre}}
                    transformOrigin={{vertical: Vertical.Bottom, horizontal: Horizontal.Centre}}
                    offset={{x: 0, y: -10}}
                    popDirection="up"
                    buttonLabel={t('*:str_ButtonOK')}
                    buttonFunction={() => setShowCantEditColourPopout(false)}
                    onClose={() => setShowCantEditColourPopout(false)}
                    arrow
                    className="max-w-[var(--anchorElementWidth)]"
                  />
                </>
              )}
            </div>
          )}
        </div>
        <HelpTip
          open={!isSmallScreen && introTipOpen}
          id={'backgroundsHelpTip-dragBackgrounds'}
          message={t('str_MessageHowToUseBackgroundsPalette', {
            ns: 'helptips',
          })}
          buttonLabel={t('*:str_ButtonOK')}
          buttonFunction={() => setIntroTipOpen(false)}
          anchorId={'backgroundsListContainer'}
          anchorOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Right}}
          transformOrigin={{vertical: Vertical.Top, horizontal: Horizontal.Left}}
          offset={{x: 10, y: 0}}
          icon={<ArrowLeftTopIcon />}
          className={'max-w-[240rem]'}
          showAfter={500}
          hideAfter={5000}
          onClose={() => setIntroTipOpen(false)}
          showMode={'once'}
        />
      </ToolPaletteSection>
    );
  }
);

export default BackgroundPalette;
